import { createCashfreeOrder } from '../../services/cashfreeService';
import { SUBSCRIPTION_ROUTES } from './subscriptionRoutes';

const SDK_URL = 'https://sdk.cashfree.com/js/v3/cashfree.js';
let sdkPromise;

export function cashfreeMode(gateway = {}) {
  const mode = String(gateway.environment || process.env.REACT_APP_CASHFREE_ENVIRONMENT || '').trim().toLowerCase();
  return ['sandbox', 'production'].includes(mode) ? mode : null;
}

export function supportedWebGateways(gateways = []) {
  return gateways.filter(gateway => Number(gateway.payment_gateway_id) > 0 && (
    ['razorpay', 'bank-transfer', 'upi-transfer', 'cashfree'].includes(gateway.slug)
  ));
}

export function loadCashfree() {
  if (typeof window.Cashfree === 'function') return Promise.resolve();
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const finish = (error) => {
      clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
      if (error) { script.remove(); reject(error); }
      else resolve();
    };
    const timer = setTimeout(() => finish(new Error('Cashfree checkout timed out. Please try again.')), 15000);
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => finish(typeof window.Cashfree === 'function' ? null : new Error('Cashfree checkout did not initialize.'));
    script.onerror = () => finish(new Error('Unable to load Cashfree checkout. Please try again.'));
    document.body.appendChild(script);
  }).catch(error => { sdkPromise = undefined; throw error; });
  return sdkPromise;
}

export async function openCashfree(providerOrder, gateway = {}) {
  const mode = cashfreeMode(providerOrder) || cashfreeMode(gateway);
  if (!mode) throw new Error('Cashfree checkout is not configured.');
  if (!providerOrder?.payment_session_id) throw new Error('Cashfree payment session is missing.');
  await loadCashfree();
  return window.Cashfree({ mode }).checkout({
    paymentSessionId: providerOrder.payment_session_id, redirectTarget: '_modal',
  });
}

export async function startCashfreePayment({ orderId, gateway, pendingState, navigate, resultPath = SUBSCRIPTION_ROUTES.paymentResult, amount }) {
  // Fail before creating a provider order when the SDK is blocked or unavailable.
  await loadCashfree();
  const providerOrder = await createCashfreeOrder(orderId, gateway.payment_gateway_id);
  if (providerOrder.amount == null || !Number.isFinite(Number(providerOrder.amount)) ||
      Number(providerOrder.amount) <= 0 || Math.round(Number(providerOrder.amount) * 100) !== Math.round(Number(amount) * 100)) {
    throw new Error('Payment amount changed. Refresh your order before paying.');
  }
  if (String(providerOrder.currency || '').toUpperCase() !== 'INR') throw new Error('Unsupported payment currency.');
  if (providerOrder.environment && cashfreeMode(gateway) && cashfreeMode(providerOrder) !== cashfreeMode(gateway)) {
    throw new Error('Cashfree environment changed. Refresh payment methods before paying.');
  }
  // Persist only recovery context, never a session token or gateway credentials.
  sessionStorage.setItem('subscriptionPurchasePending', JSON.stringify(pendingState));
  try {
    await openCashfree(providerOrder, gateway);
  } finally {
    // Completion, cancellation and SDK errors all need authoritative server status.
    navigate(resultPath, { state: pendingState });
  }
}
